import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Bot,
  Briefcase,
  ChevronDown,
  Landmark,
  LogOut,
  Megaphone,
  Settings,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useUiStore } from "@/stores/ui.store";
import { NAV_ITEMS, NAV_GROUP_LABELS, type NavGroup, type NavItem } from "@/lib/constants";
import { can, isAgencyAdmin, type CurrentUser } from "@/lib/permissions";
import { Avatar } from "@/ui";
import { useCurrentUser, useLogout } from "@/features/auth/auth.hooks";
import { BUILD_SHA, BUILD_DATE, BUILD_VERSION, BUILD_MARKER } from "@/lib/build-marker";
import { logoutToLogin } from "./sidebar.logic";

/**
 * Two-level navigation (refonte spec §5): 7 stable destinations — Accueil
 * (dashboard) + 6 collapsible spaces. The active space auto-expands; the
 * others collapse so the sidebar reads as an OS, not a list of 24 pages.
 * NAV_ITEMS stays the single source of truth (routes/permissions untouched —
 * route-permissions.ts derives URL guards from it).
 */
const GROUP_ORDER: NavGroup[] = [
  "operations",
  "marketing",
  "compta",
  "reports",
  "ai",
  "team",
  // Spec §6 — « Paramètres » est une section autonome, toujours en dernier.
  "settings",
];

const GROUP_ICON: Record<NavGroup, LucideIcon> = {
  operations: Briefcase,
  marketing: Megaphone,
  compta: Landmark,
  reports: BarChart3,
  ai: Bot,
  team: Users,
  settings: Settings,
};

/**
 * Groupes rendus à plat : une section à entrée unique n'a rien à replier, donc
 * elle s'affiche comme destination de premier niveau (même traitement
 * qu'Accueil) au lieu d'un accordéon « Paramètres > Paramètres ».
 */
const FLAT_GROUPS: NavGroup[] = ["settings"];

const HOME_ROUTE = "/dashboard";

function itemVisible(item: NavItem, user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    (!item.permission || can(user, item.permission)) &&
    (!item.adminOnly || isAgencyAdmin(user)) &&
    (!item.restrictRoles || item.restrictRoles.some((r) => user.roles.includes(r as never)))
  );
}

/** Longest NAV_ITEMS route prefix matching the current pathname → its group. */
function groupForPathname(pathname: string): NavGroup | null {
  let best: { len: number; group: NavGroup } | null = null;
  for (const item of NAV_ITEMS) {
    if (item.to === HOME_ROUTE) continue;
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.len) best = { len: item.to.length, group: item.group };
    }
  }
  return best?.group ?? null;
}

export function Sidebar() {
  const { data: currentUser } = useCurrentUser();
  // La session optimiste (instant shell) seed l'utilisateur dès le démarrage,
  // donc `currentUser` est disponible immédiatement sur une session valide.
  const user = currentUser ?? null;
  const identity = currentUser ?? null;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const location = useLocation();

  const activeGroup = useMemo(() => groupForPathname(location.pathname), [location.pathname]);
  const [openGroups, setOpenGroups] = useState<Partial<Record<NavGroup, boolean>>>({});
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);

  // The space that owns the current page always reveals its sub-navigation.
  useEffect(() => {
    if (activeGroup) setOpenGroups((prev) => ({ ...prev, [activeGroup]: true }));
  }, [activeGroup]);

  const homeItem = NAV_ITEMS.find((i) => i.to === HOME_ROUTE);
  const showHome = homeItem ? itemVisible(homeItem, user) : false;

  return (
    <aside
      className={cn(
        // Desktop (≥ md) : rail fixe inchangé — seul flip RTL : border-e.
        "w-[var(--sidebar-w)] min-w-[var(--sidebar-w)] bg-bg2 border-e border-border flex flex-col overflow-y-auto scroll-thin z-[100] overscroll-contain",
        // Mobile : volet off-canvas au-dessus du voile d'AppShell (z-200).
        // `invisible` fermé = hors tabulation SANS démonter (openGroups survit).
        "max-md:fixed max-md:inset-y-0 max-md:start-0 max-md:z-[210] max-md:shadow-floating",
        "max-md:transition-transform max-md:duration-200 motion-reduce:transition-none",
        !sidebarOpen && "max-md:-translate-x-full max-md:rtl:translate-x-full max-md:invisible",
      )}
    >
      <div className="flex items-center gap-2.5 px-4 h-[var(--topbar-h)] border-b border-border shrink-0">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary text-white text-[13px] font-bold shadow-sm">
          F
        </div>
        <span className="font-display text-[15px] font-bold text-text">Fad Agency</span>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label={t("common.close")}
          className="ms-auto md:hidden flex h-10 w-10 items-center justify-center rounded-sm text-text3 hover:bg-bg4"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      <nav className="flex-1 px-2.5 py-3">
        {showHome && homeItem && (
          <NavLink
            to={homeItem.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-[13px] font-semibold mb-1 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                isActive
                  ? "bg-primary-dim text-primary"
                  : "text-text2 hover:bg-bg4/70 hover:text-text",
              )
            }
          >
            <homeItem.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{t("nav.home")}</span>
          </NavLink>
        )}

        {GROUP_ORDER.map((group) => {
          const items = NAV_ITEMS.filter(
            (i) => i.group === group && i.to !== HOME_ROUTE && itemVisible(i, user),
          );
          if (items.length === 0) return null;
          const GroupIcon = GROUP_ICON[group];

          // Section autonome (Paramètres) : rendue comme destination directe.
          if (FLAT_GROUPS.includes(group)) {
            return (
              <div key={group} className="mt-2 pt-2 border-t border-border">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-[13px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        isActive
                          ? "bg-primary-dim text-primary"
                          : "text-text2 hover:bg-bg4/70 hover:text-text",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </div>
            );
          }

          const isActiveGroup = group === activeGroup;
          const open = openGroups[group] ?? isActiveGroup;
          return (
            <div key={group} className="mb-0.5">
              <button
                type="button"
                onClick={() => setOpenGroups((prev) => ({ ...prev, [group]: !open }))}
                aria-expanded={open}
                className={cn(
                  "flex w-full items-center gap-2.5 px-2.5 py-2 rounded-sm text-[13px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  isActiveGroup ? "text-text" : "text-text2 hover:bg-bg4/70 hover:text-text",
                )}
              >
                <GroupIcon className={cn("h-4 w-4 shrink-0", isActiveGroup && "text-primary")} />
                <span className="flex-1 text-left">{t(NAV_GROUP_LABELS[group].key)}</span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 text-text3 transition-transform",
                    open && "rotate-180",
                  )}
                />
              </button>

              {open && (
                <div className="ml-[19px] border-l border-border pl-2 py-0.5 mb-1 animate-fadeIn">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 px-2 py-[7px] rounded-sm text-[12.5px] font-medium mb-px transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                          isActive
                            ? "bg-primary-dim text-primary font-semibold"
                            : "text-text3 hover:bg-bg4/70 hover:text-text2",
                        )
                      }
                    >
                      <span className="flex-1 truncate">{t(item.labelKey)}</span>
                      {item.badge && (
                        <span
                          className={cn(
                            "text-[10px] font-semibold px-1.5 rounded-full",
                            item.badge.tone === "danger"
                              ? "bg-danger-dim text-danger"
                              : "bg-primary-dim text-primary",
                          )}
                        >
                          {item.badge.value}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-2.5 border-t border-border shrink-0">
        <div className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-sm">
          {identity ? (
            <>
              <Avatar name={identity.name} color="violet" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-text truncate">{identity.name}</div>
                <div className="text-[10px] text-text3">
                  {identity.roles.map((r) => t(`common.roles.${r}`)).join(", ")}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="h-8 w-8 rounded-full bg-surface2 animate-pulse" aria-hidden />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3 w-24 rounded-sm bg-surface2 animate-pulse" aria-hidden />
                <div className="h-2 w-14 rounded-sm bg-surface2 animate-pulse" aria-hidden />
                <span className="sr-only">{t("states.loading")}</span>
              </div>
            </>
          )}
        </div>
        <div className="text-[9px] text-text3 px-2 mb-1 truncate" title={BUILD_MARKER}>
          v{BUILD_VERSION} · {BUILD_SHA} · {BUILD_DATE}
        </div>
        <button
          type="button"
          onClick={() => logoutToLogin(logout, navigate)}
          className="mt-1 flex w-full items-center gap-2.5 rounded-sm border border-transparent px-2.5 py-2 text-[12px] font-semibold text-text3 transition-colors hover:border-danger/25 hover:bg-danger-dim hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/35"
          aria-label={t("common.logout")}
          title={t("common.logout")}
        >
          <LogOut className="h-[15px] w-[15px] shrink-0" />
          <span className="truncate">{t("common.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
